import { useGetAllAdminAppraisalQuery } from "@/app/features/employee/performance/performance.api";
import useAuth from "@/hooks/useAuth";
import useDiscloser from "@/hooks/useDiscloser";
import { EyeIcon, LaptopPerformanceIcon } from "@/icons";
import { formatDate, normalizeColumnValue } from "@/utils";
import { Button, Space, type TableProps } from "antd";
import CustomTable from "../../../../components/CustomTable";
import StatusTag from "../../../../components/StatusTag";
import { FILTER_KEYS } from "../../../../constants/keys.constants";
import { useAppFilters } from "../../../../hooks";
import { countParPageSerialNumber } from "../../../../utils/global.utils";
import LogViewer from "../../../Performance/_components/LogViewer";
import ApproveAppraisalByAdmin from "./_components/ApproveAppraisalByAdmin";
import ViewAppraisal from "@/pages/Performance/_components/ViewAppraisal";

const STATUS_CONFIG = [
  {
    label: "Not Started",
    value: "NOT_STARTED",
    className: "status status-slate",
  },
  {
    label: "KPI Set-Up Start",
    value: "PENDING",
    className: "status status-fuchsia",
  },
  {
    label: "Approved",
    value: "APPROVED",
    className: "status status-success",
  },
  {
    label: "Submitted",
    value: "SUBMITTED",
    className: "status status-sky",
  },
  {
    label: "Rejected",
    value: "REJECTED",
    className: "status status-error",
  },
];

export default function AppraisalReport() {
  const { organizationId } = useAuth();
  const { page, limit, handlePagination, filters } = useAppFilters(
    FILTER_KEYS.PERFORMANCE_APPRAISAL_REPORT,
  );
  const [config, handleOpen, handleClose] = useDiscloser<
    "LOG" | "MANAGE" | "VIEW" | "VIEW_APPRAISAL"
  >();

  const { data, isFetching } = useGetAllAdminAppraisalQuery({
    organization_id: organizationId,
    page,
    limit,
    ...filters,
  });

  const appraisalData = data?.data?.employees || [];
  const total = data?.data?.pagination?.total || 0;

  const columns: TableProps<any>["columns"] = [
    {
      title: "SL NO.",
      align: "center",
      width: 80,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: "Plan Name",
      dataIndex: "plan_name",
      render: (val: string) => normalizeColumnValue(val, "N/A", false),
    },
    {
      title: "Employee ID",
      dataIndex: ["employee", "employee_id"],
      render: (val: string) => normalizeColumnValue(val, "N/A", false),
    },
    {
      title: "Employee Name",
      dataIndex: ["employee", "name"],
      render: (val: string) => normalizeColumnValue(val, "N/A", false),
    },
    {
      title: "Designation",
      dataIndex: ["employee", "designation"],
      render: (val: string) => normalizeColumnValue(val, "N/A", false),
    },
    {
      title: "Department",
      dataIndex: ["employee", "department"],
      render: (val: string) => normalizeColumnValue(val, "N/A", false),
    },
    {
      title: "Line Manager",
      dataIndex: ["employee", "manager"],
      render: (val: string) => normalizeColumnValue(val, "N/A", false),
    },
    {
      title: "Status",
      dataIndex: "appraisal_status",
      align: "center",
      render: (val: string) => (
        <StatusTag
          value={val}
          config={STATUS_CONFIG}
          shouldCapitalize={false}
        />
      ),
    },
    {
      title: "Plan Period",
      dataIndex: "plan_period",
      align: "center",
      render: (val) => normalizeColumnValue(val),
    },
    {
      align: "center",
      title: "Due Date",
      dataIndex: "appraisal_due_date",
      className: "text-nowrap",
      render: (val) => (val ? formatDate(val) : "N/A"),
    },
    {
      title: "Actions",
      dataIndex: "mapping_id",
      align: "end",
      render: (id: string, record: any) => (
        <Space size={8}>
          <Button
            size="small"
            className="action-btn"
            icon={<EyeIcon size={20} />}
            disabled={record.appraisal_approval_status === "NOT_STARTED"}
            onClick={() =>
              handleOpen({
                type:
                  record.appraisal_status === "APPROVED"
                    ? "VIEW_APPRAISAL"
                    : "VIEW",
                data: {
                  mappingId: id,
                  status: record.appraisal_status,
                  planName: record.plan_name,
                },
              })
            }
          />
          <Button
            size="small"
            type="default"
            className="btn-primary-bordered h-8 flex items-center gap-2"
            onClick={() =>
              handleOpen({
                type: "LOG",
                data: {
                  mappingId: id,
                  planName: record.plan_name,
                  dateTime: record.created_at
                    ? formatDate(record.created_at)
                    : "",
                },
              })
            }
          >
            <LaptopPerformanceIcon className="text-theme-primary size-4" />
            Log View
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <CustomTable
        page={page}
        limit={limit}
        hideRowSelect
        columns={columns}
        dataSource={appraisalData}
        onPaginate={handlePagination}
        customTitle="Appraisal Report"
        loading={isFetching}
        total={total}
        filterSection={<></>}
      />

      <LogViewer
        open={config.open && config.type === "LOG"}
        onClose={handleClose}
        id={config.data?.mappingId}
        planName={config.data?.planName}
        dateTime={config.data?.dateTime}
      />

      <ApproveAppraisalByAdmin
        open={config.open && config.type === "VIEW"}
        onClose={handleClose}
        mappingId={config.data?.mappingId}
        status={config.data?.status}
      />

      <ViewAppraisal
        open={config.open && config.type === "VIEW_APPRAISAL"}
        onClose={handleClose}
        mappingId={config.data?.mappingId}
        planName={config.data?.planName}
        source="TEAM"
        from="admin"
      />
    </div>
  );
}
